<script setup lang="ts">
import { getUrlParamsFromCriteria } from '@@/shared/property/url-serp';
import type { PropertySearchCriteria } from '@@/shared/property/search-criteria';
import type { IadAppRoute } from '~/application';
import { useDebouncedCloneOnce } from '~/composables/editable-clone-once';

const emit = defineEmits<{
  alert: [PropertySearchCriteria]
}>();

const isPopoverOpen = ref(false);

// Current prices
const route = useRoute();
const { t } = useI18n();
const { propertySearchCriteria } = usePropertySearchCriteria(route);
const { modelValue: editablePropertySearchCriteria, debouncedModelValue: debouncedPropertySearchCriteria, resetFromSource } = useDebouncedCloneOnce(propertySearchCriteria);

const { largeAmount } = useIadFormat();
const popoverTitle = computed(() => {
  const hasNoPrice = propertySearchCriteria.value.priceMin == null && propertySearchCriteria.value.priceMax == null;
  if (hasNoPrice) return t(`property_serp.filters.price`);
  return [
    propertySearchCriteria.value.priceMin,
    propertySearchCriteria.value.priceMax,
  ].filter(Boolean)
    .map(largeAmount)
    .join(' - ');
});

// Updated result count
const { locale } = useI18n();

const updatedUrlParams = computed(() => {
  return getUrlParamsFromCriteria(locale.value, debouncedPropertySearchCriteria.value);
});
const updatedSerpSlug = toRef(() => updatedUrlParams.value.serpSlug);
const updatedQuery = toRef(() => updatedUrlParams.value.query);
const { data } = useSearchProperties(updatedSerpSlug, updatedQuery);
const propertyCount = computed(() => data.value?.totalItems ?? 0);

// Button link
const buttonLink = computed<IadAppRoute<`serp-serpSlug`>>(() => {
  return {
    name: 'serp-serpSlug',
    params: { serpSlug: updatedSerpSlug.value },
    query: { ...updatedQuery.value, page: undefined },
  };
});

watch(isPopoverOpen, (open) => {
  if (open) resetFromSource();
});

const { filtersIncludeSummary } = usePropertySearchCriteriaCount(editablePropertySearchCriteria, ['priceMin', 'priceMax']);
function onApplyFilters() {
  trackEvent({
    event: 'search',
    event_name: 'filter_interaction',
    total_filters: filtersIncludeSummary.value.count,
    selected_filter_types: filtersIncludeSummary.value.keys,
  });
  isPopoverOpen.value = false;
}

function onAlert() {
  isPopoverOpen.value = false;
  emit('alert', editablePropertySearchCriteria.value);
}
</script>

<template>
  <IadPopover
    v-model="isPopoverOpen"
    :text="popoverTitle"
  >
    <IadTitle
      :text="$t('property_serp.filters.price')"
      variant="x-small"
      class="pb-xs"
    />
    <PropertyFilterInputPrice
      v-model="editablePropertySearchCriteria"
      variant="popover"
    />
    <PropertyFilterButton
      :open="isPopoverOpen"
      class="mt-m"
      :property-count
      :to="buttonLink"
      @update:open="onApplyFilters"
      @alert="onAlert"
    />
  </IadPopover>
</template>
